import { normalizeCategoryName, slugifyCategoryName } from '../utils/categoryUtils';
import * as apiClient from './apiClient';

/**
 * PillPoint Product Data Loader
 * Loads and transforms the scraped PillPoint data for React consumption
 */

const USD_TO_EUR_RATE = 0.92;

/**
 * Generate SEO-friendly slug like LloydsPharmacy
 * Example: "viagra-connect-sildenafil-50mg-4-tablets"
 */
const generateSeoSlug = (product) => {
  const parts = [];
  
  // Get product name (cleaned)
  const name = (product.name || '').toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim();
  
  if (name) {
    parts.push(name);
  }
  
  // Try to extract active ingredient from tags or attributes
  const tags = product.tags || [];
  const activeIngredientTag = tags.find(tag => 
    tag.name && !tag.name.toLowerCase().includes('buy') && 
    !tag.name.toLowerCase().includes(name.split(' ')[0])
  );
  
  if (activeIngredientTag && !name.toLowerCase().includes(activeIngredientTag.name.toLowerCase())) {
    parts.push(activeIngredientTag.name.toLowerCase().replace(/[^a-z0-9]/g, ''));
  }
  
  // Get first pack size from pricing options
  const priceOptions = product.pricing?.priceOptions || [];
  if (priceOptions.length > 0) {
    const firstOption = priceOptions[0].qty || '';
    // Extract number and unit (e.g., "100 pills" -> "100-tablets")
    const match = firstOption.match(/(\d+)\s*(pills?|tablets?|capsules?|strips?)/i);
    if (match) {
      const count = match[1];
      const unit = match[2].toLowerCase().replace('pill', 'tablet');
      parts.push(`${count}-${unit.endsWith('s') ? unit : unit + 's'}`);
    }
  }
  
  // Join and create slug
  const slug = parts.join('-')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
  
  return slug || product.slug || `product-${product.id}`;
};

const toNumber = (value) => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
};

const convertUsdToEur = (value) => {
  const usdValue = toNumber(value);
  if (!usdValue) {
    return 0;
  }
  return Number((usdValue * USD_TO_EUR_RATE).toFixed(2));
};

const convertPricingToEur = (pricing = {}) => {
  const originalCurrency = pricing.currency || 'USD';

  const toNumeric = (value) => toNumber(value ?? 0);
  const maybeConvert = (value) => {
    const numeric = toNumeric(value);
    if (!numeric) return 0;
    return originalCurrency === 'EUR' ? numeric : convertUsdToEur(numeric);
  };

  const baseValue = pricing.price ?? pricing.min ?? pricing.max ?? 0;
  const minValue = pricing.min ?? baseValue;
  const maxValue = pricing.max ?? baseValue;

  const converted = {
    ...pricing,
    currency: 'GBP',
    currencySymbol: '£',
    originalCurrency,
    originalPrice: toNumeric(baseValue),
    originalMin: toNumeric(minValue),
    originalMax: toNumeric(maxValue),
    price: maybeConvert(baseValue),
    regular: maybeConvert(pricing.regular ?? baseValue),
    sale: maybeConvert(pricing.sale ?? baseValue),
    min: maybeConvert(minValue),
    max: maybeConvert(maxValue)
  };

  if (Array.isArray(pricing.priceOptions) && pricing.priceOptions.length > 0) {
    converted.priceOptions = pricing.priceOptions.map((option) => ({
      ...option,
      originalPrice: toNumeric(option.price),
      price: maybeConvert(option.price)
    }));
  }

  return converted;
};

const enhanceCategory = (category = {}) => {
  const name = normalizeCategoryName(category.name || '');
  const slug = category.slug || slugifyCategoryName(name);
  return {
    ...category,
    name,
    slug
  };
};

// Cache for products
let cachedProducts = null;
let cacheTimestamp = null;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes
const API_PRODUCT_LIMIT = 500;

/**
 * Load product catalog from backend API with caching.
 */
export const loadPillPointProducts = async () => {
  try {
    // Check if we have valid cached data
    if (cachedProducts && cacheTimestamp && (Date.now() - cacheTimestamp < CACHE_DURATION)) {
      return cachedProducts;
    }

    const result = await apiClient.fetchProducts({ limit: API_PRODUCT_LIMIT });
    const data = { products: result.data || [] };
    
    // Update cache
    cachedProducts = data;
    cacheTimestamp = Date.now();
    
    return data;
  } catch (error) {
    // If fetch fails but we have cached data, return it
    if (cachedProducts) {
      return cachedProducts;
    }
    throw error;
  }
};

const sanitizePillPointContent = (html = '') => {
  if (!html) return '';
  let content = html;
  content = content.replace(/<\/?(script|style|iframe|form|link|meta)[^>]*>/gi, '');
  content = content.replace(/<img[^>]*>/gi, '');
  content = content.replace(/\[(related_products|vc_[^\]]+)\b[^\]]*\]/gi, '');
  content = content.replace(/<div[^>]+class=['"][^'"]*(related|upsell|products)[^'"]*['"][^>]*>[\s\S]*?<\/div>/gi, '');
  content = content.replace(/<section[^>]+class=['"][^'"]*(related|upsell|products)[^'"]*['"][^>]*>[\s\S]*?<\/section>/gi, '');
  content = content.replace(/<p[^>]*>[^<]*(related products|upsell|customers also bought)[^<]*<\/p>/gi, '');
  content = content.replace(/<a[^>]*>/gi, '');
  content = content.replace(/<\/a>/gi, '');
  content = content.replace(/https?:\/\/[\S]+/gi, '');
  content = content.replace(/&nbsp;/gi, ' ');
  content = content.replace(/\s{2,}/g, ' ');
  return content.trim();
};

const ensureUkSeoNote = (html = '') => {
  if (!html) return '';
  const hasUkMention = /\b(uk|united kingdom|britain|england|scotland|wales|northern ireland)\b/i.test(html);
  if (hasUkMention) {
    return html;
  }
  return `${html}<p>Available with discreet UK delivery and pharmacist guidance.</p>`;
};

/**
 * Transform PillPoint product to legacy format for compatibility
 */
export const transformPillPointProduct = (pillpointProduct) => {
  const mediaSources = Array.isArray(pillpointProduct.images) && pillpointProduct.images.length > 0
    ? pillpointProduct.images
    : (pillpointProduct.imagery || []);

  const normalizedImages = mediaSources
    .map((img) => {
      if (typeof img === 'string') {
        return {
          src: img,
          localPath: img,
          alt: pillpointProduct.name,
          name: pillpointProduct.name
        };
      }

      const src = img?.localPath || img?.remoteUrl || img?.src;
      if (!src) return null;
      return {
        ...img,
        src,
        localPath: img.localPath || undefined,
        remoteUrl: img.remoteUrl || undefined,
        alt: img.alt || pillpointProduct.name,
        name: img.name || pillpointProduct.name
      };
    })
    .filter(Boolean);

  const imagePath = normalizedImages[0]?.src || '/images/placeholder.svg';

  const normalizedCategories = (pillpointProduct.categories || []).map(enhanceCategory);
  const mainCategoryEntry = normalizedCategories[0] || { name: 'Uncategorized', slug: 'uncategorized' };

  const convertedPricing = convertPricingToEur(pillpointProduct.pricing || {});
  const priceMin = convertedPricing.min ?? 0;
  const priceMax = convertedPricing.max ?? priceMin;

  const hasVariants = pillpointProduct.variations && pillpointProduct.variations.length > 0;
  const rawFullDescription = pillpointProduct.description || pillpointProduct.wpContent || pillpointProduct.wpExcerpt || '';
  const rawShortDescription = pillpointProduct.shortDescription || pillpointProduct.wpExcerpt || '';
  const sanitizedFullDescription = ensureUkSeoNote(sanitizePillPointContent(rawFullDescription || rawShortDescription));
  const sanitizedShortDescription = ensureUkSeoNote(sanitizePillPointContent(rawShortDescription || rawFullDescription));
  const summaryText = stripHtml(sanitizedShortDescription || sanitizedFullDescription) || `Clinically trusted ${mainCategoryEntry.name.toLowerCase()} therapy available for UK delivery.`;

  // Generate SEO-friendly slug like LloydsPharmacy
  const seoSlug = generateSeoSlug(pillpointProduct);

  return {
    id: `PP${String(pillpointProduct.id).padStart(5, '0')}`,
    productId: `PP${String(pillpointProduct.id).padStart(5, '0')}`,
    name: pillpointProduct.name,
    slug: seoSlug,
    originalSlug: pillpointProduct.slug,
    category: mainCategoryEntry.name,
    categorySlug: mainCategoryEntry.slug,
    categories: normalizedCategories,
    description: summaryText,
    shortDescription: sanitizedShortDescription,
    fullDescription: sanitizedFullDescription,
    image: imagePath,
    images: normalizedImages,
    inStock: pillpointProduct.stock?.inStock ?? true,
    hasVariants,
    variants: (pillpointProduct.variations || []).map((variation) => {
      const variationSources = Array.isArray(variation.images) && variation.images.length > 0
        ? variation.images
        : (variation.imagery || []);

      const variationImages = variationSources
        .map((img) => {
          if (typeof img === 'string') {
            return { src: img, localPath: img, alt: variation.name, name: variation.name };
          }
          const src = img?.localPath || img?.remoteUrl || img?.src;
          if (!src) return null;
          return {
            ...img,
            src,
            localPath: img.localPath || undefined,
            remoteUrl: img.remoteUrl || undefined,
            alt: img.alt || variation.name,
            name: img.name || variation.name
          };
        })
        .filter(Boolean);

      const variantPricing = convertPricingToEur(variation.price || {});
      const variantPrice = variantPricing.price ?? convertUsdToEur(variation.price?.price ?? 0);

      return {
        id: variation.id,
        name: variation.name,
        price: variantPrice,
        priceRange: {
          min: variantPricing.min ?? variantPrice,
          max: variantPricing.max ?? variantPrice,
          currency: 'GBP',
          originalCurrency: variantPricing.originalCurrency || 'USD'
        },
        pricing: variantPricing,
        currency: 'GBP',
        currencySymbol: '£',
        inStock: variation.stock?.inStock ?? true,
        attributes: variation.attributes,
        image: variationImages[0]?.src,
        images: variationImages
      };
    }),
    priceRange: {
      min: priceMin,
      max: priceMax,
      currency: 'GBP',
      originalCurrency: convertedPricing.originalCurrency || 'USD'
    },
    price: convertedPricing.price ?? priceMin,
    pricing: convertedPricing,
    currency: 'GBP',
    currencySymbol: '£',
    rating: pillpointProduct.rating?.average || 0,
    reviewCount: pillpointProduct.rating?.count || 0,
    tags: pillpointProduct.tags?.map((tag) => tag.name) || [],
    brands: pillpointProduct.brands?.map((brand) => brand.name) || [],
    attributes: pillpointProduct.attributes || [],
    permalink: pillpointProduct.permalink,
    originalPricing: pillpointProduct.pricing || null,
    _pillpointOriginal: pillpointProduct
  };
};

/**
 * Strip HTML tags from description
 */
const stripHtml = (html) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
};

/**
 * Get filtered PillPoint products
 */
export const getFilteredPillPointProducts = async (filters = {}) => {
  const data = await loadPillPointProducts();
  let products = data.products || [];
  
  const { searchTerm, category, priceMin, priceMax, inStock } = filters;
  
  // Filter by search term
  if (searchTerm) {
    const term = searchTerm.toLowerCase();
    products = products.filter((product) => 
      product.name.toLowerCase().includes(term) ||
      product.slug.toLowerCase().includes(term) ||
      product.categories?.some((cat) => {
        const name = normalizeCategoryName(cat.name).toLowerCase();
        return name.includes(term) || (cat.slug || '').toLowerCase().includes(term);
      }) ||
      product.tags?.some((tag) => tag.name.toLowerCase().includes(term))
    );
  }
  
  // Filter by category
  if (category) {
    const categorySlug = slugifyCategoryName(category);
    const categoryName = normalizeCategoryName(category).toLowerCase();
    products = products.filter((product) => 
      product.categories?.some((cat) => {
        const slug = (cat.slug || '').toLowerCase();
        const name = normalizeCategoryName(cat.name).toLowerCase();
        return slug === categorySlug || name === categoryName;
      })
    );
  }
  
  // Filter by price range
  if (priceMin !== undefined || priceMax !== undefined) {
    products = products.filter(product => {
      const price = product.pricing?.min || product.pricing?.price || 0;
      return price >= (priceMin || 0) && price <= (priceMax || Infinity);
    });
  }
  
  // Filter by stock status
  if (inStock !== undefined) {
    products = products.filter(product => product.stock?.inStock === inStock);
  }
  
  // Transform to legacy format
  return products.map(transformPillPointProduct);
};

const mapApiProductToLegacy = (p) => ({
  ...p,
  id: p.productId || p._id,
  currencySymbol: '£',
  currency: 'GBP',
  category: p.categories?.[0]?.name || 'Uncategorized',
  categorySlug: p.categories?.[0]?.slug || 'uncategorized',
  image: p.images?.[0]?.src || '/images/placeholder.svg',
  inStock: p.stock?.inStock !== false,
  price: p.pricing?.price || 0,
  priceRange: { min: p.pricing?.min || 0, max: p.pricing?.max || 0, currency: 'GBP' },
  rating: p.rating?.average || 0,
  reviewCount: p.rating?.count || 0,
  fullDescription: p.fullDescription || '',
  shortDescription: p.shortDescription || '',
});

/**
 * Get single PillPoint product by ID or slug
 */
export const getPillPointProductById = async (idOrSlug) => {
  try {
    const slugResult = await apiClient.fetchProductBySlug(idOrSlug);
    const product = slugResult?.data;
    return product ? mapApiProductToLegacy(product) : null;
  } catch {
    // If slug lookup fails, try productId lookup.
  }

  try {
    const idResult = await apiClient.fetchProductById(idOrSlug);
    const product = idResult?.data;
    return product ? mapApiProductToLegacy(product) : null;
  } catch {
    return null;
  }
};

/**
 * Get all unique categories from PillPoint products
 */
export const getPillPointCategories = async () => {
  const result = await apiClient.fetchCategories();
  const categories = Array.isArray(result.data) ? result.data : [];
  return categories.map((category) => ({
    ...category,
    id: category._id || category.id || category.slug,
    count: category.productCount ?? category.count ?? 0,
  }));
};

/**
 * Get unique brands/manufacturers from PillPoint products
 */
export const getPillPointBrands = async () => {
  const products = await loadAllProducts();

  const brandMap = new Map();

  const addBrand = (name) => {
    const normalized = typeof name === 'string' ? name.trim() : '';
    if (!normalized) return;

    const slug = slugifyCategoryName(normalized);
    if (!brandMap.has(slug)) {
      brandMap.set(slug, {
        name: normalized,
        slug,
        count: 0
      });
    }

    const entry = brandMap.get(slug);
    entry.count = (entry.count || 0) + 1;
  };

  products.forEach((product) => {
    product.brands?.forEach((brand) => {
      if (!brand) return;
      if (typeof brand === 'string') {
        addBrand(brand);
      } else if (brand.name) {
        addBrand(brand.name);
      }
    });

    if (product.brand) {
      addBrand(product.brand);
    }

    if (product.manufacturer) {
      addBrand(product.manufacturer);
    }

    if (product._pillpointOriginal?.brands) {
      product._pillpointOriginal.brands.forEach((brand) => {
        if (typeof brand === 'string') {
          addBrand(brand);
        } else if (brand?.name) {
          addBrand(brand.name);
        }
      });
    }
  });

  return Array.from(brandMap.values()).sort((a, b) => {
    if (b.count !== a.count) {
      return b.count - a.count;
    }
    return a.name.localeCompare(b.name);
  });
};

/**
 * Get product statistics
 */
export const getPillPointStats = async () => {
  const products = await loadAllProducts();
  
  return {
    totalProducts: products.length,
    inStockProducts: products.filter((product) => product.inStock ?? product.stock?.inStock).length,
    productsWithVariants: products.filter((product) => product.variants?.length > 0 || product.variations?.length > 0).length,
    totalImages: products.reduce((sum, product) => sum + (product.images?.length || 0), 0),
    scrapedAt: null,
    categories: await getPillPointCategories()
  };
};

// Export unified loader from backend API.
export const loadAllProducts = async () => {
  const result = await apiClient.fetchProducts({ limit: API_PRODUCT_LIMIT });
  return (result.data || []).map(mapApiProductToLegacy);
};

export const PillPointDataLoader = {
  loadPillPointProducts,
  transformPillPointProduct,
  getFilteredPillPointProducts,
  getPillPointProductById,
  getPillPointCategories,
  getPillPointBrands,
  getPillPointStats,
  loadAllProducts
};
